'use client';

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import type { ReactNode } from 'react';
import { isEmpty } from 'lodash';
import Pusher from 'pusher-js';
import type { ChannelAuthorizationData } from 'pusher-js/types/src/core/auth/options';
import type { Transport } from 'pusher-js/types/src/core/config';
import { authPusher } from 'src/services/auth';

const ENABLED_TRANSPORTS: Transport[] = ['ws', 'wss'];

interface PusherConfigContextValue {
	getPusherInstance: (instanceKey: string) => Pusher | undefined;
}

const pusherConfigContext = createContext<PusherConfigContextValue>({
	getPusherInstance: () => undefined,
});

interface PusherConfigProviderProps {
	children: ReactNode;
}

const getEnv = (val?: string) => {
	const trimmed = val?.trim();
	return trimmed && !trimmed.startsWith('__REPLACE_WITH_') ? trimmed : '';
};

export const PusherConfigProvider = ({
	children,
}: PusherConfigProviderProps) => {
	const pusherRef = useRef<Record<string, Pusher>>({});

	const getPusherInstance = useCallback(
		(instanceKey: string): Pusher | undefined => {
			const pusherKey = getEnv(process.env.NEXT_PUBLIC_APP_PUSHER_KEY);
			const pusherHost = getEnv(process.env.NEXT_PUBLIC_APP_PUSHER_HOST);
			const pusherPort = getEnv(process.env.NEXT_PUBLIC_APP_PUSHER_PORT);

			if (
				pusherKey &&
				pusherHost &&
				isEmpty(pusherRef?.current?.[instanceKey])
			) {
				const pusher = new Pusher(pusherKey, {
					wsHost: pusherHost,
					wsPort: Number(pusherPort),
					wssPort: Number(pusherPort),
					forceTLS: true,
					enabledTransports: ENABLED_TRANSPORTS,
					cluster: '',
					authorizer: (channel) => ({
						authorize: (socketId, callback) =>
							authPusher({
								socket_id: socketId,
								channel_name: channel.name,
							})
								.then((res) => {
									if (!res) return;
									callback(null, res.data as ChannelAuthorizationData);
								})
								.catch(() => {}),
					}),
				});

				pusher.connection.bind('connected', () => {
					if (pusher?.connection?.socket_id) {
						sessionStorage.setItem('socket_id', pusher.connection.socket_id);
					}
				});
				pusherRef.current[instanceKey] = pusher;
			}

			return pusherRef?.current?.[instanceKey];
		},
		[],
	);

	useEffect(
		() => () => {
			Object.keys(pusherRef.current).forEach((key) => {
				if (pusherRef.current[key]) {
					pusherRef.current[key].disconnect();
				}
			});
		},
		[],
	);

	const memoizedValue = useMemo<PusherConfigContextValue>(
		() => ({
			getPusherInstance,
		}),
		[getPusherInstance],
	);

	return (
		<pusherConfigContext.Provider value={memoizedValue}>
			{children}
		</pusherConfigContext.Provider>
	);
};

export const usePusherConfig = (): PusherConfigContextValue =>
	useContext(pusherConfigContext);
